import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import AdminLayout from "@/components/feature/AdminLayout";
import { adminCustomers } from "@/mocks/admin";
import type { AdminCustomer } from "@/mocks/admin";
import { mockOrders } from "@/mocks/orders";
import CustomerDetailModal from "./components/CustomerDetailModal";

export default function AdminCustomersPage() {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState<AdminCustomer | null>(null);

  const filteredCustomers = useMemo(() => {
    return adminCustomers.filter(c => {
      if (searchQuery === "") return true;
      const q = searchQuery.toLowerCase();
      return c.name.toLowerCase().includes(q) ||
        c.email.toLowerCase().includes(q) ||
        c.phone.includes(q);
    });
  }, [searchQuery]);

  const getCustomerOrders = (customer: AdminCustomer) => {
    return mockOrders.slice(0, Math.floor(Math.random() * 4) + 1).map(o => ({ ...o }));
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground-950">{t("admin.customers")}</h1>
            <p className="text-sm text-foreground-500 mt-1">{filteredCustomers.length} customers found</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-sm">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            placeholder="Search by name, email, or phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
          />
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: "Total Customers", value: adminCustomers.length, icon: "ri-group-line", color: "bg-primary-100 text-primary-600" },
            { label: "Active", value: adminCustomers.filter(c => c.status === "active").length, icon: "ri-user-follow-line", color: "bg-accent-100 text-accent-600" },
            { label: "Inactive", value: adminCustomers.filter(c => c.status === "inactive").length, icon: "ri-user-unfollow-line", color: "bg-secondary-100 text-secondary-600" },
            { label: "Avg. Spent", value: `$${Math.round(adminCustomers.reduce((s, c) => s + c.totalSpent, 0) / adminCustomers.length).toLocaleString()}`, icon: "ri-money-dollar-circle-line", color: "bg-primary-100 text-primary-600" },
          ].map((stat) => (
            <div key={stat.label} className="bg-background-50 rounded-xl p-4 border border-background-200/70">
              <div className={`w-8 h-8 rounded-lg ${stat.color} flex items-center justify-center mb-3`}>
                <i className={stat.icon}></i>
              </div>
              <p className="text-xl font-bold text-foreground-950 font-heading">{stat.value}</p>
              <p className="text-xs text-foreground-500 mt-0.5">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Customers Table */}
        <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-background-200/70">
                  {["Customer", "Email", "Phone", "Orders", "Total Spent", "Last Order", "Status"].map((h) => (
                    <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.map((customer) => (
                  <tr
                    key={customer.id}
                    onClick={() => setSelectedCustomer(customer)}
                    className="border-b border-background-100 hover:bg-background-100/50 transition-colors cursor-pointer"
                  >
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-secondary-200 overflow-hidden flex-shrink-0">
                          <img src={customer.avatarUrl} alt={customer.name} className="w-full h-full object-cover" loading="lazy" />
                        </div>
                        <span className="text-sm font-medium text-foreground-900 whitespace-nowrap">{customer.name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="text-sm text-foreground-600">{customer.email}</span>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="text-sm text-foreground-600 whitespace-nowrap">{customer.phone}</span>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap">{customer.totalOrders}</span>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap">${customer.totalSpent.toFixed(2)}</span>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="text-sm text-foreground-500 whitespace-nowrap">{customer.lastOrderDate || "N/A"}</span>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className={`inline-flex text-xs font-semibold px-2.5 py-1 rounded-full capitalize whitespace-nowrap ${
                        customer.status === "active"
                          ? "bg-accent-100 text-accent-800"
                          : "bg-foreground-100 text-foreground-600"
                      }`}>
                        {customer.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredCustomers.length === 0 && (
            <div className="py-16 text-center">
              <i className="ri-user-search-line text-3xl text-foreground-300 mb-3 block"></i>
              <p className="text-sm text-foreground-500">No customers match your search</p>
            </div>
          )}
        </div>

        {/* Customer Detail Modal */}
        {selectedCustomer && (
          <CustomerDetailModal
            customer={selectedCustomer}
            orders={getCustomerOrders(selectedCustomer)}
            onClose={() => setSelectedCustomer(null)}
          />
        )}
      </div>
    </AdminLayout>
  );
}